import { darken } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import styled from 'styled-components';

interface ButtonProps {
  size?: 'smaller' | 'small' | 'medium' | 'large';
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  view?: 'primary' | 'secondary' | 'tertiary' | 'quartiary' | 'quinary';
  onClick?: (event?: any) => void;
  style?: React.CSSProperties;
  value?: string;
  name?: string;
  color?: string;
  hoverColor?: { bg: string; color: string };
}

interface ButtonCoreProps {
  size?: 'smaller' | 'small' | 'medium' | 'large';
  type?: 'button' | 'submit' | 'reset';
  view?: 'primary' | 'secondary' | 'tertiary' | 'quartiary' | 'quinary';
  variant?: 'outline' | 'text' | 'contained';
  color?: string;
  hoverColor?: { bg: string; color: string };
}

export const Button = ({ label, view = 'primary', loading, icon, ...props }: ButtonProps) => {
  if (view === 'secondary') {
    return (
      <SecondaryButtonStyle disabled={loading} {...props}>
        <span>{label}</span>
      </SecondaryButtonStyle>
    );
  }

  if (view === 'tertiary') {
    return (
      <TertiaryButtonStyle disabled={loading} {...props}>
        {loading && <CircularProgress size={18} color="inherit" />}
        {!loading && icon && (
          <>
            {icon} {label}
          </>
        )}
        {!loading && !icon && label}
      </TertiaryButtonStyle>
    );
  }

  if (view === 'quartiary') {
    return (
      <QuartiaryButtonStyle disabled={loading} {...props}>
        {label}
      </QuartiaryButtonStyle>
    );
  }

  if (view === 'quinary') {
    return (
      <QuinaryButtonStyle {...props}>
        <span>{label}</span>
      </QuinaryButtonStyle>
    );
  }

  return (
    <PrimaryButtonStyle disabled={loading} {...props}>
      {loading && <CircularProgress size={18} color="inherit" />}
      {!loading && icon && (
        <>
          {icon} {label}
        </>
      )}
      {!loading && !icon && label}
    </PrimaryButtonStyle>
  );
};

const PrimaryButtonStyle = styled.button<ButtonCoreProps>`
  position: relative;
  z-index: 2;
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  padding: 10px 5px;
  border-radius: 4px;
  color: #fff;
  border: none;
  transition: all 0.3s ease-out;
  font-weight: bold;
  cursor: pointer;
  ${({ size }) => {
    if (size === 'small') {
      return `font-size: 9px;`;
    }
    if (size === 'smaller') {
      return `font-size: 7px;`;
    }
  }}
  &:disabled {
    opacity: 0.3;
  }
  &:hover {
    background-color: ${darken(`#1F9ECC`, 0.2)};
  }
  svg {
    margin-right: 5px;
    vertical-align: middle;
    width: 17px;
    height: 17px;
  }
`;

const SecondaryButtonStyle = styled.button`
  padding: 10px 5px;
  display: inline-block;
  vertical-align: middle;
  box-sizing: border-box;
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
  border: none;
  background: transparent;
  transition-duration: 0.2s;
  transition: 0.2s all linear;
  &:hover {
    & > span {
      text-decoration: none;
      color: #000;
    }
  }

  & > span {
    color: var(--primary-color);
    text-decoration: underline;
    transition: 0.2s all linear;
  }
`;

const TertiaryButtonStyle = styled.button<ButtonCoreProps>`
  padding: 10px 5px;
  display: inline-block;
  font-weight: bold;
  vertical-align: middle;
  border-radius: 4px;
  box-sizing: border-box;
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
  border: 2px solid ${(props) => props?.color ?? '#ff7c0a'};
  color: ${(props) => props?.color ?? '#ff7c0a'};
  background: transparent;
  transition-duration: 0.2s;
  transition: 0.2s all linear;
  &:hover {
    & > span {
      text-decoration: none;
      color: #000;
    }
  }
  & > span {
    color: ${(props) => props?.hoverColor?.color ?? '#ff7c0a'};
    text-decoration: underline;
    transition: 0.2s all linear;
  }
  ${({ size }) => {
    if (size === 'small') {
      return `font-size: 9px;`;
    }
    if (size === 'smaller') {
      return `font-size: 7px;`;
    }
  }}
  &:disabled {
    opacity: 0.3;
  }
  &:hover {
    color: #f5f5f5;
    background-color: ${(props) => props?.hoverColor?.bg ?? '#ff7c0a'};
  }
  svg {
    margin-right: 5px;
    vertical-align: middle;
    width: 17px;
    height: 17px;
  }
`;

const QuartiaryButtonStyle = styled.button`
  border: 1px solid #fff;
  background-color: transparent;
  border-radius: 4px;
  color: #fff;
  padding: 10px 5px;
  font-size: 16px;
  cursor: pointer;
`;

const QuinaryButtonStyle = styled.button`
  border-color: transparent;
  background-color: transparent;
  padding: 5px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    & > span {
      color: ${darken(`#1F9ECC`, 0.2)};
    }
  }

  & > span {
    color: var(--primary-color);

    transition: 0.2s all linear;
  }
`;
